import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  alternativeScenario, amortizationScenarios, annualEquivalent,
  cetFromCashflow, compare, cumulativeSeries, diagnosticScore,
  extraCostImpact, financedExtras, impliedMonthlyRate,
  impliedSacRateFromFirstPayment, money, monthlyExtras, pct,
  priceSchedule, sacSchedule, summarize,
} from '../lib/finance';
import { getBcbRate } from '../lib/bcb';
import './juros-abusivos.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const N = (x) => {
  const s = String(x ?? '').trim().replace(/\s/g, '');
  if (!s) return 0;
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return Number(s) || 0;
};
const boolText = (v) => (v === 'yes' ? 'Sim' : v === 'no' ? 'Não' : 'Não informado');

const defaults = {
  kind: 'vehicle', system: 'price', date: '', asset: 90000, down: 20000,
  creditReceived: '', financed: 70000, months: 48, payment: 2100, rate: '',
  insuranceUpfront: 0, insuranceUpfrontFinanced: false, insuranceMonthly: 0,
  iof: 0, iofFinanced: false, registration: 0, registrationFinanced: false,
  appraisal: 0, appraisalFinanced: false, cadastreFee: 0, cadastreFeeFinanced: false,
  otherFees: 0, otherFeesFinanced: false, otherMonthly: 0,
  insuranceVoluntary: 'unknown', insuranceChoice: 'unknown', insuranceRequired: 'unknown',
  paidInstallments: 12, extraAmortization: 5000,
  clientName: '', clientPhone: '', clientEmail: '', bankName: '',
  contractNumber: '', notes: '',
};

function escapeHtml(s = '') {
  return String(s).replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m]));
}
function buildReportHtml({ calc, f, bcb }) {
  const rows = calc.rows.slice(0, 120).map((r) => `<tr><td>${r.k}</td><td>${money(r.payment)}</td><td>${money(calc.mExtras)}</td><td>${money(r.interest)}</td><td>${money(r.amortization)}</td><td>${money(r.balance)}</td></tr>`).join('');
  const reasons = calc.diagnosis.reasons.map((r) => `<li>${escapeHtml(r)}</li>`).join('');
  const today = new Intl.DateTimeFormat('pt-BR').format(new Date());
  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>Relatório - Juros Abusivos Não</title>
<style>body{font-family:Arial,sans-serif;color:#17283a;margin:34px;line-height:1.45}h1,h2{font-family:Georgia,serif;color:#0d3152}h1{font-size:30px;margin-bottom:2px}.brand{border-bottom:3px solid #cda85c;padding-bottom:14px;margin-bottom:24px}.muted{color:#6d7f8f;font-size:12px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}.box{border:1px solid #dce5eb;border-radius:10px;padding:14px;margin:8px 0}.box strong{display:block;font-size:19px}.warning{background:#fff8e9;border-left:4px solid #cda85c;padding:12px}.high{background:#fff1ef;border-left-color:#b8463f}.score{font-size:28px;font-weight:bold;color:#0d3152}table{width:100%;border-collapse:collapse;font-size:11px;margin-top:12px}th,td{padding:6px;border-bottom:1px solid #e4e9ed;text-align:right}th:first-child,td:first-child{text-align:left}th{background:#f3f6f8}.pagebreak{page-break-before:always}.foot{margin-top:26px;font-size:10px;color:#71808d}ul{padding-left:20px}@media print{body{margin:18mm}}</style></head><body>
<div class="brand"><div class="muted">RELATÓRIO TÉCNICO INFORMATIVO</div><h1>Juros Abusivos Não</h1><div class="muted">Gerado em ${today} — Dr. Edson Bispo (OAB/SP 338.390)</div></div>
<h2>Identificação</h2><div class="grid">
<div class="box"><span class="muted">Cliente</span><strong>${escapeHtml(f.clientName || 'Não informado')}</strong></div>
<div class="box"><span class="muted">Instituição</span><strong>${escapeHtml(f.bankName || 'Não informada')}</strong></div>
<div class="box"><span class="muted">Contrato</span><strong>${escapeHtml(f.contractNumber || 'Não informado')}</strong></div>
<div class="box"><span class="muted">Data da contratação</span><strong>${escapeHtml(f.date || 'Não informada')}</strong></div></div>
<h2>Resumo executivo</h2><div class="grid">
<div class="box"><span class="muted">Crédito efetivo</span><strong>${money(calc.netReceived)}</strong></div>
<div class="box"><span class="muted">Total financiado</span><strong>${money(calc.pv)}</strong></div>
<div class="box"><span class="muted">Taxa utilizada</span><strong>${pct(calc.rate)} a.m.</strong></div>
<div class="box"><span class="muted">CET reconstruído aprox.</span><strong>${calc.cet ? pct(calc.cet.monthly) + ' a.m.' : '—'}</strong></div>
<div class="box"><span class="muted">Encargos financiados</span><strong>${money(calc.extrasFinanced)}</strong></div>
<div class="box"><span class="muted">Juros projetados</span><strong>${money(calc.sum.interest)}</strong></div></div>
<div class="warning ${calc.diagnosis.level.includes('elevada') ? 'high' : ''}"><div class="score">${calc.diagnosis.score}/100</div><strong>Classificação técnica: ${escapeHtml(calc.diagnosis.level)}</strong><ul>${reasons}</ul>
<div class="muted">A pontuação é triagem técnica. Não representa decisão judicial, perícia ou probabilidade de êxito.</div></div>
<h2>Comparação de cenário</h2><div class="grid">
<div class="box"><span class="muted">Contrato informado</span><strong>${money(calc.sum.payments)}</strong><div>Juros: ${money(calc.sum.interest)}</div></div>
<div class="box"><span class="muted">Referência BACEN</span><strong>${bcb && calc.alt.summary ? money(calc.alt.summary.payments) : '—'}</strong><div>${bcb ? `SGS ${bcb.series} • ${pct(bcb.value)} a.m.` : 'Referência não consultada'}</div></div>
<div class="box"><span class="muted">Diferença projetada</span><strong>${bcb && calc.alt.summary ? money(calc.sum.payments - calc.alt.summary.payments) : '—'}</strong></div>
<div class="box"><span class="muted">Diferença de juros</span><strong>${bcb && calc.alt.summary ? money(calc.sum.interest - calc.alt.summary.interest) : '—'}</strong></div></div>
<h2>Observações do usuário</h2><div class="box">${escapeHtml(f.notes || 'Nenhuma observação informada.')}</div>
<div class="pagebreak"></div><h2>Memória de cálculo</h2><table><thead><tr><th>#</th><th>Parcela fin.</th><th>Seguro/outros</th><th>Juros</th><th>Amortização</th><th>Saldo</th></tr></thead><tbody>${rows}</tbody></table>
<div class="foot">Relatório informativo gerado a partir dos dados digitados. Taxa BACEN é referência comparativa. O valor oficial de quitação, CET contratual e conclusão jurídica devem ser conferidos nos documentos da operação.</div>
</body></html>`;
}
function openPrintableReport({ calc, f, bcb }) {
  const w = window.open('', '_blank', 'noopener,noreferrer,width=960,height=760');
  if (!w) return false;
  w.document.open();
  w.document.write(buildReportHtml({ calc, f, bcb }));
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 350);
  return true;
}
function downloadReportHtml({ calc, f, bcb }) {
  const blob = new Blob([buildReportHtml({ calc, f, bcb })], { type: 'text/html;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'relatorio-juros-abusivos-nao.html';
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// SEO helpers ---------------------------------------------------------------
function setMeta(name, content, attr = 'name') {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}
function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

// FAQ — perguntas e respostas efetivamente exibidas na página.
// Linguagem simples, sem promessa de resultado, sem afirmar ilegalidade.
const FAQ_ITEMS = [
  {
    q: 'Preciso pagar alguma coisa para usar a calculadora?',
    a: 'Não. O Raio-X é gratuito e não exige cadastro para calcular. Nome, telefone e e-mail só são solicitados se você quiser gerar um relatório personalizado ou entrar em contato para análise documental.',
  },
  {
    q: 'A ferramenta afirma que meu contrato é ilegal ou que os juros são abusivos?',
    a: 'Não. A calculadora apresenta indicadores técnicos e um sinal comparativo em relação à referência do Banco Central. Concluir por eventual abusividade depende de análise documental caso a caso, feita por advogado.',
  },
  {
    q: 'De onde vem a taxa usada na comparação?',
    a: 'Da própria API pública do Banco Central (SGS), séries oficiais correspondentes a financiamento de veículos e imóveis. A consulta é feita para o mês da contratação informada, sem substituição por valores fixos.',
  },
  {
    q: 'O CET reconstruído aqui substitui o CET do contrato?',
    a: 'Não. É uma estimativa matemática a partir dos dados digitados por você, útil para conferência. O CET oficial da operação está no contrato e deve prevalecer para qualquer análise formal.',
  },
  {
    q: 'A diferença projetada entre cenários é o valor que tenho a receber?',
    a: 'Não. Trata-se apenas de comparação entre o fluxo informado e o cenário com a taxa de referência do Banco Central. Eventuais restituições ou revisões só podem ser avaliadas após análise dos documentos e do contexto.',
  },
  {
    q: 'O que acontece quando eu clico em "Quero analisar meu contrato"?',
    a: 'Você é encaminhado ao WhatsApp do Dr. Edson Bispo (OAB/SP 338.390) com um resumo do seu Raio-X. O envio depende de consentimento explícito seu; nenhum dado é transmitido automaticamente.',
  },
];

export default function JurosAbusivos() {
  const [f, setF] = useState(defaults);
  const [bcb, setBcb] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState('simple');
  const [showMemory, setShowMemory] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Load WhatsApp number from backend config
  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setWhatsappNumber(r.data.whatsapp_number || '')).catch(() => {});
  }, []);

  // SEO
  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'Calculadora de Juros Abusivos | Financiamento de Veículos e Imóveis';
    const md = setMeta(
      'description',
      'Analise seu financiamento de veículo ou imóvel, compare taxas, CET, seguro prestamista, amortização e referência do Banco Central.'
    );
    const canonical = setLink('canonical', `${window.location.origin}/juros-abusivos-nao`);
    const ogT = setMeta('og:title', 'Calculadora de Juros Abusivos | Advedson Bispo', 'property');
    const ogD = setMeta(
      'og:description',
      'Raio-X técnico do seu financiamento com comparação BACEN. Análise informativa por Dr. Edson Bispo — OAB/SP 338.390.',
      'property'
    );
    const ogU = setMeta('og:url', `${window.location.origin}/juros-abusivos-nao`, 'property');

    // FAQPage JSON-LD — apenas para Q&A efetivamente exibidas
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.setAttribute('data-jan-faq', '1');
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((it) => ({
        '@type': 'Question',
        name: it.q,
        acceptedAnswer: { '@type': 'Answer', text: it.a },
      })),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = originalTitle;
      md.remove(); canonical.remove(); ogT.remove(); ogD.remove(); ogU.remove();
      ld.remove();
    };
  }, []);

  const calc = useMemo(() => {
    const pv = N(f.financed), n = N(f.months), totalPayment = N(f.payment), mExtras = monthlyExtras(f);
    const financialPayment = Math.max(0, totalPayment - mExtras);
    const declared = N(f.rate);
    const implied = f.system === 'sac'
      ? impliedSacRateFromFirstPayment(pv, financialPayment, n)
      : impliedMonthlyRate(pv, financialPayment, n);
    const rate = declared || implied || 0;
    const rows = f.system === 'sac'
      ? sacSchedule(pv, rate, n)
      : priceSchedule(pv, rate, n, declared ? null : (financialPayment > 0 ? financialPayment : null));
    const modeledFirstPayment = rows[0]?.payment || 0;
    const paymentMismatch = financialPayment > 0 && modeledFirstPayment > 0
      ? Math.abs(modeledFirstPayment - financialPayment) / financialPayment * 100
      : 0;
    const sum = summarize(rows), ref = bcb?.value || 0, cmp = compare(rate, ref);
    const extrasFinanced = financedExtras(
      Object.fromEntries(Object.entries(f).map(([k, v]) => [k, typeof v === 'string' ? N(v) : v]))
    );
    const extrasAll = N(f.insuranceUpfront) + N(f.iof) + N(f.registration) + N(f.appraisal) + N(f.cadastreFee) + N(f.otherFees);
    const extraImpact = extraCostImpact(extrasFinanced, rate, n, f.system);
    const netReceived = N(f.creditReceived) || Math.max(0, pv - extrasFinanced);
    const cet = cetFromCashflow({ netReceived, rows, monthlyExtra: mExtras });
    const monthlyExtrasTotal = mExtras * rows.length;
    const totalOutflow = sum.payments + monthlyExtrasTotal;
    const amort = amortizationScenarios({
      system: f.system, pv, monthlyPct: rate, totalMonths: n,
      paidInstallments: N(f.paidInstallments), extraAmount: N(f.extraAmortization),
      contractPayment: financialPayment > 0 ? financialPayment : null,
    });
    const alt = bcb ? alternativeScenario({ system: f.system, pv, months: n, referenceRate: bcb.value }) : { rows: [], summary: null };
    const insuranceFlags = (f.insuranceVoluntary === 'no' ? 1 : 0) + (f.insuranceChoice === 'no' ? 1 : 0) + (f.insuranceRequired === 'yes' ? 1 : 0);
    const diagnosis = diagnosticScore({
      contractRate: rate, referenceRate: bcb?.value || 0, extrasFinanced,
      principal: pv, cetMonthly: cet?.monthly ?? null, declaredRate: declared, insuranceFlags,
    });
    const contractCum = cumulativeSeries(rows);
    const altCum = alt.rows.length ? cumulativeSeries(alt.rows) : [];
    return {
      pv, n, totalPayment, financialPayment, mExtras, declared, implied, rate, rows, sum, cmp,
      impact: alt.summary ? sum.payments - alt.summary.payments : null,
      extrasFinanced, extrasAll, extraImpact, netReceived, cet, monthlyExtrasTotal, totalOutflow,
      amort, alt, diagnosis, contractCum, altCum, paymentMismatch, modeledFirstPayment,
    };
  }, [f, bcb]);

  async function lookup() {
    setLoading(true); setErr('');
    try {
      const r = await getBcbRate(f.kind, f.date);
      setBcb(r);
      if (!r) setErr('Não encontrei valor para o mês informado.');
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  function csv() {
    const h = 'Parcela;Prestação financeira;Seguro/outros mensais;Saída total;Juros;Amortização;Saldo\n';
    const body = calc.rows.map((r) => [r.k, r.payment, calc.mExtras, r.payment + calc.mExtras, r.interest, r.amortization, r.balance]
      .map((x) => (typeof x === 'number' ? x.toFixed(2).replace('.', ',') : x)).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\ufeff' + h + body], { type: 'text/csv;charset=utf-8' }));
    a.download = 'raio-x-financiamento.csv';
    a.click();
  }

  const property = f.kind !== 'vehicle';

  return (
    <div className="jan-scope" data-testid="juros-abusivos-page">
      <main>
        <header className="hero">
          <div className="brand">
            <span className="mark">JAN</span>
            <div><strong>Juros Abusivos Não</strong><small>Raio-X técnico do financiamento</small></div>
          </div>
          <div className="heroText">
            <span className="eyebrow">VEÍCULOS • IMÓVEIS • CET • AMORTIZAÇÃO</span>
            <h1>Entenda seu financiamento antes de tomar uma decisão.</h1>
            <p>Em poucos passos, veja juros, seguro, tarifas, saldo devedor e uma comparação técnica com a referência do Banco Central.</p>
            <div className="heroActions">
              <a href="#calc" className="primary" data-testid="jan-hero-cta">Analisar meu financiamento</a>
              <span>Gratuito • sem cadastro para calcular</span>
            </div>
          </div>
        </header>

        <section className="trustStrip">
          <div><strong>1</strong><span>Informe os dados do contrato</span></div>
          <div><strong>2</strong><span>Veja o Raio-X financeiro</span></div>
          <div><strong>3</strong><span>Decida se quer análise documental</span></div>
        </section>

        <section id="calc" className="shell">
          <div className="form card contractCard">
            <div className="stepHeader">
              <div>
                <span className="stepPill">PASSO 1</span>
                <h2>Dados principais do contrato</h2>
                <p>Comece pelo que você encontra facilmente no contrato ou carnê. Se não souber a taxa, nós a reconstruímos matematicamente.</p>
              </div>
              <div className="modeSwitch">
                <button className={mode === 'simple' ? 'active' : ''} onClick={() => setMode('simple')} data-testid="jan-mode-simple">Essencial</button>
                <button className={mode === 'advanced' ? 'active' : ''} onClick={() => setMode('advanced')} data-testid="jan-mode-advanced">Detalhado</button>
              </div>
            </div>
            <div className="grid2">
              <Field label="Modalidade">
                <select value={f.kind} onChange={(e) => { set('kind', e.target.value); setBcb(null); }} data-testid="jan-kind">
                  <option value="vehicle">Veículo — pessoa física</option>
                  <option value="property_market">Imóvel — taxa de mercado</option>
                  <option value="property_regulated">Imóvel — taxa regulada</option>
                </select>
              </Field>
              <Field label="Sistema">
                <select value={f.system} onChange={(e) => set('system', e.target.value)} data-testid="jan-system">
                  <option value="price">PRICE / prestação fixa</option>
                  <option value="sac">SAC</option>
                </select>
              </Field>
              <Field label="Data da contratação"><input type="date" value={f.date} onChange={(e) => set('date', e.target.value)} data-testid="jan-date" /></Field>
              {mode === 'advanced' && <Field label="Valor do bem (R$)"><input value={f.asset} onChange={(e) => set('asset', e.target.value)} /></Field>}
              {mode === 'advanced' && <Field label="Entrada (R$)"><input value={f.down} onChange={(e) => set('down', e.target.value)} /></Field>}
              {mode === 'advanced' && <Field label="Crédito efetivamente utilizado/recebido (R$)"><input value={f.creditReceived} onChange={(e) => set('creditReceived', e.target.value)} /></Field>}
              <Field label="Valor financiado (R$)"><input value={f.financed} onChange={(e) => set('financed', e.target.value)} data-testid="jan-financed" /></Field>
              <Field label="Quantidade de parcelas"><input value={f.months} onChange={(e) => set('months', e.target.value)} data-testid="jan-months" /></Field>
              <Field label={f.system === 'sac' ? 'Valor da 1ª parcela (R$)' : 'Valor da parcela (R$)'}><input value={f.payment} onChange={(e) => set('payment', e.target.value)} data-testid="jan-payment" /></Field>
              <Field label="Taxa de juros mensal (% a.m.) — se souber"><input placeholder="opcional" value={f.rate} onChange={(e) => set('rate', e.target.value)} data-testid="jan-rate" /></Field>
            </div>
          </div>

          <div className={`card extrasCard ${mode === 'simple' ? 'simpleExtras' : ''}`}>
            <div className="stepHeader">
              <div>
                <span className="stepPill">PASSO 2</span>
                <h2>Seguro e outros custos</h2>
                <p>Se você não souber algum valor, pode deixar em zero e continuar.</p>
              </div>
            </div>
            <p className="intro">Marque &ldquo;incorporado ao financiamento&rdquo; somente quando o custo entrou no valor financiado.</p>
            <Cost label="Seguro prestamista — prêmio único" value={f.insuranceUpfront} financed={f.insuranceUpfrontFinanced} setValue={(v) => set('insuranceUpfront', v)} setFinanced={(v) => set('insuranceUpfrontFinanced', v)} />
            <Cost label="IOF" value={f.iof} financed={f.iofFinanced} setValue={(v) => set('iof', v)} setFinanced={(v) => set('iofFinanced', v)} />
            {mode === 'advanced' && <Cost label="Registro / gravame" value={f.registration} financed={f.registrationFinanced} setValue={(v) => set('registration', v)} setFinanced={(v) => set('registrationFinanced', v)} />}
            {mode === 'advanced' && <Cost label="Avaliação do bem" value={f.appraisal} financed={f.appraisalFinanced} setValue={(v) => set('appraisal', v)} setFinanced={(v) => set('appraisalFinanced', v)} />}
            {mode === 'advanced' && <Cost label="Tarifa de cadastro" value={f.cadastreFee} financed={f.cadastreFeeFinanced} setValue={(v) => set('cadastreFee', v)} setFinanced={(v) => set('cadastreFeeFinanced', v)} />}
            {mode === 'advanced' && <Cost label="Outros custos iniciais" value={f.otherFees} financed={f.otherFeesFinanced} setValue={(v) => set('otherFees', v)} setFinanced={(v) => set('otherFeesFinanced', v)} />}
            <div className="grid2 topGap">
              <Field label="Seguro cobrado mensalmente (R$)"><input value={f.insuranceMonthly} onChange={(e) => set('insuranceMonthly', e.target.value)} /></Field>
              <Field label="Outros encargos mensais (R$)"><input value={f.otherMonthly} onChange={(e) => set('otherMonthly', e.target.value)} /></Field>
            </div>
            <div className="insuranceQuestions">
              <h3>Como o seguro foi oferecido?</h3>
              <Question label="Você escolheu contratar o seguro?" value={f.insuranceVoluntary} onChange={(v) => set('insuranceVoluntary', v)} />
              <Question label="Foi informado de que poderia escolher outra seguradora?" value={f.insuranceChoice} onChange={(v) => set('insuranceChoice', v)} />
              <Question label="Disseram que o seguro era necessário para aprovar o financiamento?" value={f.insuranceRequired} onChange={(v) => set('insuranceRequired', v)} />
            </div>
            <button className="secondary" disabled={!f.date || loading} onClick={lookup} data-testid="jan-bcb-btn">
              {loading ? 'Consultando BACEN…' : 'Comparar com o Banco Central'}
            </button>
            {err && <p className="error" role="alert">{err}</p>}
          </div>
        </section>

        <section className="wide card">
          <div className="sectionHead">
            <div>
              <span className="eyebrow dark">SEU RESULTADO</span>
              <h2>3. Raio-X do financiamento</h2>
              <p className="intro">Primeiro mostramos os números. Depois explicamos o que merece atenção.</p>
            </div>
            <span className="badge">V5</span>
          </div>
          <div className="metrics six" aria-live="polite">
            <Metric label="Crédito efetivo" value={money(calc.netReceived)} sub="valor utilizado/recebido" />
            <Metric label="Total financiado" value={money(calc.pv)} sub="base contratual informada" />
            <Metric label="Custos financiados" value={money(calc.extrasFinanced)} sub="seguros/tarifas incorporados" />
            <Metric label="Parcela financeira" value={money(calc.financialPayment)} sub="parcela total menos custos mensais" />
            <Metric label="Seguro/outros mensais" value={money(calc.mExtras)} sub="destacados da prestação" />
            <Metric label="Saída total projetada" value={money(calc.totalOutflow)} sub="fluxo informado/projetado" />
          </div>

          <div className="plainSummary">
            <div className="summaryIcon">✓</div>
            <div>
              <strong>Leitura rápida</strong>
              <p>
                {bcb
                  ? `Sua taxa está ${calc.cmp && calc.cmp.rel > 0 ? calc.cmp.rel.toFixed(1).replace('.', ',') + '% acima' : 'próxima ou abaixo'} da referência consultada. `
                  : 'Consulte a referência do Banco Central para completar a comparação. '}
                {calc.extrasFinanced > 0
                  ? `Você também informou ${money(calc.extrasFinanced)} em custos incorporados ao financiamento.`
                  : 'Não foram identificados custos financiados nos campos preenchidos.'}
              </p>
            </div>
          </div>
          {calc.paymentMismatch > 2 && f.rate && (
            <div className="signal high">
              <b>Confira os dados digitados.</b>
              <p>A taxa informada e o valor da parcela não fecham matematicamente entre si: a diferença estimada é de {calc.paymentMismatch.toFixed(1).replace('.', ',')}%. Revise valor financiado, taxa, prazo, seguro e demais custos antes de interpretar o resultado.</p>
            </div>
          )}
          <div className="split">
            <div className="panel">
              <h3>Taxa e juros</h3>
              <Key label="Taxa usada" value={`${pct(calc.rate)} a.m.`} />
              <Key label="Equivalente anual" value={`${pct(annualEquivalent(calc.rate))} a.a.`} />
              <Key label="Taxa implícita reconstruída" value={calc.implied == null ? '—' : `${pct(calc.implied)} a.m.`} />
              <Key label="Juros projetados" value={money(calc.sum.interest)} />
              <Key label="Referência BACEN" value={bcb ? `${pct(bcb.value)} a.m. — SGS ${bcb.series}` : 'Não consultada'} />
            </div>
            <div className="panel">
              <h3>Custo efetivo estimado</h3>
              <Key label="CET reconstruído (aprox.)" value={calc.cet ? `${pct(calc.cet.monthly)} a.m.` : '—'} />
              <Key label="CET anual equivalente (aprox.)" value={calc.cet ? `${pct(calc.cet.annual)} a.a.` : '—'} />
              <Key label="Custos iniciais informados" value={money(calc.extrasAll)} />
              <Key label="Custos mensais no prazo" value={money(calc.monthlyExtrasTotal)} />
              <p className="fine">CET reconstruído é uma estimativa matemática a partir dos dados digitados. Não substitui o CET oficial do contrato.</p>
            </div>
            <div className="panel accent">
              <h3>Quanto custa financiar os encargos?</h3>
              <Key label="Encargos incorporados ao principal" value={money(calc.extraImpact.principal)} />
              <Key label="Total projetado desses encargos" value={money(calc.extraImpact.total)} />
              <Key label="Custo financeiro estimado sobre eles" value={money(calc.extraImpact.financeCost)} />
              <p className="fine">Estimativa isolando os encargos financiados sob a mesma taxa e prazo informados.</p>
            </div>
          </div>

          {calc.cmp ? (
            <div className={`signal ${calc.cmp.level.includes('elevada') ? 'high' : ''}`}>
              <b>Sinal comparativo: {calc.cmp.level}</b>
              <p>A taxa utilizada está {calc.cmp.rel.toFixed(1).replace('.', ',')}% acima da referência BACEN do período ({calc.cmp.pp.toFixed(3).replace('.', ',')} p.p. ao mês). A comparação não determina, sozinha, abusividade ou ilegalidade.</p>
              {calc.impact != null && <p>Diferença projetada entre o fluxo financeiro informado e o cenário de referência: <strong>{money(calc.impact)}</strong>.</p>}
            </div>
          ) : (
            <div className="signal neutral">
              <b>Comparação BACEN ainda não realizada.</b>
              <p>Informe a data da contratação e consulte a série correspondente à modalidade.</p>
            </div>
          )}
        </section>

        <section className="wide card">
          <h2>4. Leitura do seguro prestamista</h2>
          <div className="insuranceRead">
            <div><small>Contratação voluntária</small><strong>{boolText(f.insuranceVoluntary)}</strong></div>
            <div><small>Liberdade de escolha da seguradora</small><strong>{boolText(f.insuranceChoice)}</strong></div>
            <div><small>Apresentado como obrigatório</small><strong>{boolText(f.insuranceRequired)}</strong></div>
          </div>
          {(f.insuranceVoluntary === 'no' || f.insuranceChoice === 'no' || f.insuranceRequired === 'yes') ? (
            <div className="signal high">
              <b>Há informação que merece análise documental.</b>
              <p>As respostas sobre a contratação do seguro justificam conferir proposta, apólice, termo de adesão e contrato. A ferramenta não conclui automaticamente que houve irregularidade.</p>
            </div>
          ) : (
            <p className="fine">As respostas servem como triagem. A validade da contratação depende dos documentos e das circunstâncias concretas.</p>
          )}
          {property && <p className="notice">Financiamentos imobiliários podem envolver coberturas securitárias próprias da modalidade. O sistema mantém esses valores separados para não confundi-los com juros.</p>}
        </section>

        <section className="wide card comparisonSection">
          <div className="sectionHead">
            <div><span className="eyebrow dark">V5 • COMPARADOR</span><h2>5. Contrato × BACEN × cenário alternativo</h2></div>
            <span className="badge">TÉCNICO</span>
          </div>
          <p className="intro">O comparador usa os dados do contrato e, quando a referência BACEN foi consultada, simula a mesma operação com a taxa de referência do período. A diferença é um cenário comparativo, não um valor automaticamente devido ao consumidor.</p>
          <div className="comparisonCards">
            <div className="compareCard contract">
              <span>CONTRATO INFORMADO</span>
              <strong>{money(calc.sum.payments)}</strong>
              <small>Total financeiro projetado</small>
              <Key label="Juros" value={money(calc.sum.interest)} />
              <Key label="Taxa" value={`${pct(calc.rate)} a.m.`} />
            </div>
            <div className="compareCard reference">
              <span>REFERÊNCIA BACEN</span>
              <strong>{bcb && calc.alt.summary ? money(calc.alt.summary.payments) : '—'}</strong>
              <small>Mesmo principal e prazo</small>
              <Key label="Juros" value={bcb && calc.alt.summary ? money(calc.alt.summary.interest) : '—'} />
              <Key label="Taxa" value={bcb ? `${pct(bcb.value)} a.m.` : '—'} />
            </div>
            <div className="compareCard difference">
              <span>DIFERENÇA ENTRE CENÁRIOS</span>
              <strong>{bcb && calc.alt.summary ? money(calc.sum.payments - calc.alt.summary.payments) : '—'}</strong>
              <small>Diferença financeira projetada</small>
              <Key label="Diferença de juros" value={bcb && calc.alt.summary ? money(calc.sum.interest - calc.alt.summary.interest) : '—'} />
              <Key label="Diferença relativa de taxa" value={calc.cmp ? `${calc.cmp.rel.toFixed(1).replace('.', ',')}%` : '—'} />
            </div>
          </div>

          <div className="chartGrid">
            <div className="chartBox">
              <h3>Evolução do saldo devedor</h3>
              <LineChart contract={calc.contractCum} reference={calc.altCum} field="balance" />
              <div className="legend"><span><i className="dot contractDot"></i>Contrato</span><span><i className="dot refDot"></i>Referência</span></div>
            </div>
            <div className="chartBox">
              <h3>Juros acumulados</h3>
              <LineChart contract={calc.contractCum} reference={calc.altCum} field="interest" />
              <div className="legend"><span><i className="dot contractDot"></i>Contrato</span><span><i className="dot refDot"></i>Referência</span></div>
            </div>
          </div>

          <div className={`diagnosis ${calc.diagnosis.level.includes('elevada') ? 'diagHigh' : calc.diagnosis.level === 'atenção' ? 'diagAttention' : ''}`}>
            <div className="scoreRing"><strong>{calc.diagnosis.score}</strong><small>/100</small></div>
            <div>
              <span className="scenarioTag">DIAGNÓSTICO TÉCNICO</span>
              <h3>{calc.diagnosis.level}</h3>
              <ul>{calc.diagnosis.reasons.map((r, i) => <li key={r + i}>{r}</li>)}</ul>
              <p className="fine">A pontuação organiza sinais técnicos da triagem. Não é perícia, sentença jurídica ou probabilidade de êxito em ação judicial.</p>
            </div>
          </div>
        </section>

        <section className="wide card amortSection">
          <div className="sectionHead">
            <div><span className="eyebrow dark">AMORTIZAÇÃO</span><h2>6. Antecipação, amortização e quitação</h2></div>
            <span className="badge">TÉCNICO</span>
          </div>
          <p className="intro">Informe quantas parcelas já foram pagas e quanto você pretende amortizar hoje. O sistema compara manter a prestação para reduzir o prazo com manter o prazo para reduzir a prestação.</p>
          <div className="grid2">
            <Field label="Parcelas já pagas"><input value={f.paidInstallments} onChange={(e) => set('paidInstallments', e.target.value)} /></Field>
            <Field label="Valor da amortização extraordinária (R$)"><input value={f.extraAmortization} onChange={(e) => set('extraAmortization', e.target.value)} /></Field>
          </div>
          <div className="metrics six amortMetrics">
            <Metric label="Saldo devedor estimado hoje" value={money(calc.amort.currentBalance)} sub={`após ${calc.amort.paid} parcela(s)`} />
            <Metric label="Prazo restante" value={`${calc.amort.remainingMonths} meses`} sub="cenário original" />
            <Metric label="Amortização extraordinária" value={money(calc.amort.extra)} sub="abatimento direto do principal" />
            <Metric label="Novo saldo após amortizar" value={money(calc.amort.newBalance)} sub="antes dos juros futuros" />
            <Metric label="Quitação estimada hoje" value={money(calc.amort.payoffEstimated)} sub="saldo principal estimado" />
            <Metric label="Juros futuros no cenário original" value={money(calc.amort.futureInterestAvoided)} sub="potencialmente evitados na quitação" />
          </div>

          <div className="scenarioGrid">
            <div className="scenario">
              <span className="scenarioTag">OPÇÃO A</span>
              <h3>Reduzir o prazo</h3>
              <p>Mantém aproximadamente a lógica da prestação/amortização atual e usa o valor extra para encurtar o financiamento.</p>
              <Key label="Prazo após amortização" value={`${calc.amort.reduceTerm.length} meses`} />
              <Key label="Meses economizados" value={`${calc.amort.termMonthsSaved} meses`} />
              <Key label="Juros futuros estimados" value={money(calc.amort.reduceTermSummary.interest)} />
              <Key label="Economia estimada de juros" value={money(calc.amort.termInterestSavings)} />
            </div>
            <div className="scenario">
              <span className="scenarioTag">OPÇÃO B</span>
              <h3>Reduzir a prestação</h3>
              <p>Mantém o prazo restante e recalcula o fluxo sobre um saldo menor.</p>
              <Key label="Prazo mantido" value={`${calc.amort.reducePayment.length} meses`} />
              <Key label="Nova prestação financeira estimada" value={money(calc.amort.newPayment)} />
              <Key label="Juros futuros estimados" value={money(calc.amort.reducePaymentSummary.interest)} />
              <Key label="Economia estimada de juros" value={money(calc.amort.paymentInterestSavings)} />
            </div>
            <div className="scenario payoff">
              <span className="scenarioTag">QUITAÇÃO</span>
              <h3>Encerrar o financiamento agora</h3>
              <p>O saldo devedor estimado representa o principal remanescente. Juros futuros ainda não vencidos não integram essa estimativa matemática.</p>
              <Key label="Saldo estimado para quitação" value={money(calc.amort.payoffEstimated)} />
              <Key label="Juros futuros do cenário original" value={money(calc.amort.futureInterestAvoided)} />
              <Key label="Custos mensais futuros potencialmente evitados" value={money(calc.mExtras * calc.amort.remainingMonths)} />
              <p className="fine">O valor efetivamente cobrado para liquidação deve ser conferido com a instituição na data da quitação; seguros, tarifas, tributos, atrasos e eventos contratuais podem alterar o valor.</p>
            </div>
          </div>

          <div className="signal neutral">
            <b>Como interpretar a amortização</b>
            <p>Em condições matemáticas equivalentes, reduzir prazo costuma economizar mais juros do que apenas reduzir a prestação, porque o saldo permanece menos tempo exposto aos juros. O resultado acima usa a taxa e o sistema informados; ele não substitui o demonstrativo oficial de liquidação do credor.</p>
          </div>
        </section>

        <section className="wide card memoryCard">
          <div className="memoryHeader">
            <div>
              <span className="stepPill">DETALHES TÉCNICOS</span>
              <h2>7. Memória de cálculo</h2>
              <p className="intro">Útil para conferência profissional. O consumidor não precisa ler esta tabela para entender o resultado.</p>
            </div>
            <button className="secondary" onClick={() => setShowMemory((v) => !v)} data-testid="jan-toggle-memory">
              {showMemory ? 'Ocultar tabela' : 'Ver tabela completa'}
            </button>
          </div>
          {showMemory && (
            <>
              <div className="actions">
                <button className="secondary" onClick={csv}>Baixar CSV completo</button>
                <button className="primary" onClick={() => window.print()}>Imprimir / salvar PDF</button>
              </div>
              <div className="tableScroll">
                <table>
                  <thead>
                    <tr><th>#</th><th>Parcela financeira</th><th>Seguro/outros</th><th>Saída total</th><th>Juros</th><th>Amortização</th><th>Saldo</th></tr>
                  </thead>
                  <tbody>
                    {calc.rows.slice(0, 120).map((r) => (
                      <tr key={r.k}>
                        <td>{r.k}</td><td>{money(r.payment)}</td><td>{money(calc.mExtras)}</td>
                        <td>{money(r.payment + calc.mExtras)}</td><td>{money(r.interest)}</td>
                        <td>{money(r.amortization)}</td><td>{money(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        <section className="wide legal card">
          <span className="stepPill">IMPORTANTE</span>
          <h2>O que este resultado significa?</h2>
          <p>Esta calculadora ajuda você a entender o contrato e encontrar pontos que merecem conferência. Ela separa a matemática financeira da análise jurídica. Taxa média do Banco Central, CET reconstruído, seguro e tarifas são indicadores distintos. O resultado é uma triagem técnica e não afirma automaticamente que o contrato é ilegal, abusivo ou que existe valor certo a devolver.</p>
          <p>Quando um seguro ou tarifa é financiado, ele integra o principal informado e pode gerar custo financeiro ao longo do prazo. Quando o seguro é cobrado mensalmente, ele é destacado da parcela antes da reconstrução da taxa financeira, evitando atribuir esse valor indevidamente aos juros.</p>
        </section>

        <section className="wide card reportSection">
          <div className="sectionHead">
            <div><span className="eyebrow dark">RELATÓRIO PROFISSIONAL</span><h2>Leve seu resultado com você</h2></div>
            <span className="badge">PDF</span>
          </div>
          <p className="intro">Você pode gerar um relatório para guardar ou usar numa análise documental. Seus dados pessoais não são necessários para fazer os cálculos.</p>
          <div className="grid2">
            <Field label="Nome do cliente"><input value={f.clientName} onChange={(e) => set('clientName', e.target.value)} placeholder="Nome completo" /></Field>
            <Field label="Telefone / WhatsApp"><input value={f.clientPhone} onChange={(e) => set('clientPhone', e.target.value)} placeholder="(11) 99999-9999" /></Field>
            <Field label="E-mail"><input type="email" value={f.clientEmail} onChange={(e) => set('clientEmail', e.target.value)} placeholder="email@exemplo.com" /></Field>
            <Field label="Banco / financeira"><input value={f.bankName} onChange={(e) => set('bankName', e.target.value)} placeholder="Instituição financeira" /></Field>
            <Field label="Número do contrato"><input value={f.contractNumber} onChange={(e) => set('contractNumber', e.target.value)} placeholder="Opcional" /></Field>
            <Field label="Observações"><textarea value={f.notes} onChange={(e) => set('notes', e.target.value)} rows="4" placeholder="Ex.: seguro apresentado como obrigatório, parcela alterada, refinanciamento..." /></Field>
          </div>
          <div className="reportPreview">
            <div><small>Cliente</small><strong>{f.clientName || 'Não informado'}</strong></div>
            <div><small>Instituição</small><strong>{f.bankName || 'Não informada'}</strong></div>
            <div><small>Diagnóstico</small><strong>{calc.diagnosis.level} • {calc.diagnosis.score}/100</strong></div>
            <div><small>Taxa</small><strong>{pct(calc.rate)} a.m.</strong></div>
            <div><small>CET reconstruído</small><strong>{calc.cet ? pct(calc.cet.monthly) + ' a.m.' : '—'}</strong></div>
            <div><small>Diferença de cenário</small><strong>{bcb && calc.alt.summary ? money(calc.sum.payments - calc.alt.summary.payments) : '—'}</strong></div>
          </div>
          <div className="actions">
            <button className="primary" onClick={() => openPrintableReport({ calc, f, bcb })} data-testid="jan-report-pdf">Gerar relatório / salvar PDF</button>
            <button className="secondary" onClick={() => downloadReportHtml({ calc, f, bcb })} data-testid="jan-report-html">Baixar relatório HTML</button>
          </div>
          <p className="fine">O PDF é criado pela função &ldquo;Salvar como PDF&rdquo; da janela de impressão do navegador. Assim o sistema não envia os dados para um servidor apenas para gerar o documento.</p>
        </section>

        <section className="wide card faqSection" aria-labelledby="jan-faq-title" data-testid="jan-faq">
          <span className="stepPill">DÚVIDAS FREQUENTES</span>
          <h2 id="jan-faq-title">Perguntas rápidas antes de decidir</h2>
          <div className="faqList">
            {FAQ_ITEMS.map((it, i) => (
              <details key={it.q} className="faqItem" data-testid={`jan-faq-item-${i}`}>
                <summary>{it.q}</summary>
                <p>{it.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="wide lead card">
          <div>
            <span className="eyebrow">ANÁLISE DOCUMENTAL</span>
            <h2>Quer que o contrato seja conferido?</h2>
            <p>Se o resultado levantou dúvidas, a próxima etapa é conferir o contrato, demonstrativos, apólice e demais documentos da operação com o Dr. Edson Bispo — OAB/SP 338.390.</p>
          </div>
          <Lead calc={calc} f={f} whatsappNumber={whatsappNumber} />
        </section>

        <div className="stickyCta">
          <div>
            <strong>Já tem os dados do contrato?</strong>
            <span>Faça o Raio-X gratuitamente.</span>
          </div>
          <a href="#calc" className="primary">Analisar agora</a>
        </div>

        <footer>Juros Abusivos Não • ferramenta informativa • parceria com Advedson Bispo (OAB/SP 338.390)</footer>
      </main>
    </div>
  );
}

function Field({ label, children }) { return <label>{label}{children}</label>; }
function Metric({ label, value, sub }) { return <div><small>{label}</small><strong>{value}</strong><span>{sub}</span></div>; }
function Key({ label, value }) { return <div className="key"><span>{label}</span><strong>{value}</strong></div>; }
function Cost({ label, value, financed, setValue, setFinanced }) {
  return (
    <div className="costRow">
      <label>{label}<input value={value} onChange={(e) => setValue(e.target.value)} /></label>
      <label className="toggle">
        <input type="checkbox" checked={financed} onChange={(e) => setFinanced(e.target.checked)} /> incorporado ao financiamento
      </label>
    </div>
  );
}
function Question({ label, value, onChange }) {
  return (
    <label className="question">{label}
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="unknown">Não sei / não informado</option>
        <option value="yes">Sim</option>
        <option value="no">Não</option>
      </select>
    </label>
  );
}
function LineChart({ contract, reference, field }) {
  const width = 720, height = 240, pad = 24;
  const all = [...(contract || []).map((x) => x[field] || 0), ...(reference || []).map((x) => x[field] || 0)];
  const max = Math.max(1, ...all);
  const len = Math.max(contract?.length || 0, reference?.length || 0, 2);
  const points = (rows) => (rows || []).map((r, i) => {
    const x = pad + (i / (len - 1)) * (width - pad * 2);
    const y = height - pad - ((r[field] || 0) / max) * (height - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg className="lineChart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Gráfico comparativo">
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} className="axis" />
      <line x1={pad} y1={pad} x2={pad} y2={height - pad} className="axis" />
      {contract?.length > 1 && <polyline points={points(contract)} className="contractLine" />}
      {reference?.length > 1 && <polyline points={points(reference)} className="referenceLine" />}
    </svg>
  );
}
function Lead({ calc, f, whatsappNumber }) {
  const [ok, setOk] = useState(false);
  const msg = encodeURIComponent(
    `Olá Dr. Edson. Fiz o Raio-X no Juros Abusivos Não.
Nome: ${f.clientName || 'não informado'}
Banco/financeira: ${f.bankName || 'não informado'}
Contrato: ${f.contractNumber || 'não informado'}
Modalidade: ${f.kind}
Crédito efetivo: ${money(calc.netReceived)}
Total financiado: ${money(calc.pv)}
Custos financiados identificados: ${money(calc.extrasFinanced)}
Taxa usada: ${pct(calc.rate)} a.m.
CET reconstruído: ${calc.cet ? pct(calc.cet.monthly) + ' a.m.' : 'não calculado'}
Diagnóstico técnico: ${calc.diagnosis.level} (${calc.diagnosis.score}/100)
Saldo estimado atual: ${money(calc.amort.currentBalance)}
Gostaria de solicitar análise documental do contrato.`
  );
  const canSend = ok && whatsappNumber;
  return (
    <div className="leadBox">
      <label className="check">
        <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="jan-lead-consent" />
        Concordo em iniciar contato pelo WhatsApp e enviar meus dados voluntariamente ao Dr. Edson Bispo.
      </label>
      <a
        aria-disabled={!canSend}
        className={`primary ${!canSend ? 'disabled' : ''}`}
        href={canSend ? `https://wa.me/${whatsappNumber}?text=${msg}` : '#'}
        onClick={(e) => { if (!canSend) e.preventDefault(); }}
        target="_blank"
        rel="noreferrer"
        data-testid="jan-lead-cta"
      >
        Quero analisar meu contrato
      </a>
      {!whatsappNumber && <small>WhatsApp indisponível no momento. Recarregue a página.</small>}
    </div>
  );
}
